import React, { useEffect, useRef, useCallback } from 'react';
import styles from './drawer.module.scss';
import { buildClassName } from 'utils/build-class-name';
import { useDrawerGestures, DrawerSnapPoint } from 'hooks/use-drawer-gestures';
import { DrawerHeader } from 'components/atomic/molecules/drawer-header';

export { DrawerSnapPoint };

const DRAWER_EASING = 'cubic-bezier(0.25, 0.1, 0.25, 1)';

export const SNAP_DURATION_ONE_STEP = 350;
export const SNAP_DURATION_TWO_STEP = 500;

function getSnapTransition(from: DrawerSnapPoint, to: DrawerSnapPoint): string {
  const stepsApart =
    (from === DrawerSnapPoint.Minimized && to === DrawerSnapPoint.Full) ||
    (from === DrawerSnapPoint.Full && to === DrawerSnapPoint.Minimized);
  const duration = stepsApart ? SNAP_DURATION_TWO_STEP : SNAP_DURATION_ONE_STEP;
  return `transform ${duration}ms ${DRAWER_EASING}`;
}

const DEFAULT_DRAG_THRESHOLD = 0.15;
const DEFAULT_BORDER_RADIUS = 16;
const DEFAULT_BOX_SHADOW = '0 -2px 10px rgba(0, 0, 0, 0.15)';
const DEFAULT_BACKGROUND_COLOR = 'white';
const DEFAULT_Z_INDEX = 1000;

export interface DrawerProps {
  currentState: DrawerSnapPoint;
  onStateChange: (state: DrawerSnapPoint) => void;
  minimizedHeight: number;
  partialHeight: number;
  fullHeight: number;
  header?: React.ReactNode;
  children: React.ReactNode;
  footer?: React.ReactNode;
  showHandle?: boolean;
  enableGestures?: boolean;
  dragThreshold?: number;
  className?: string;
  borderRadius?: number;
  boxShadow?: string;
  backgroundColor?: string;
  onDragStart?: () => void;
  onDragEnd?: (finalState: DrawerSnapPoint) => void;
  zIndex?: number;
  onBackdropClick?: () => void;
  expandOnScroll?: boolean;
  bottomOffset?: number;
  /** Drag ratio %  for stopping at partial when swiping up from minimized. 0 = always transition to full. Default: 0.45 */
  partialDragRatio?: number;
}

function getStateTransform(
  state: DrawerSnapPoint,
  minimizedHeight: number,
  partialHeight: number | string
): string {
  switch (state) {
    case DrawerSnapPoint.Minimized:
      return `translateY(calc(100% - ${minimizedHeight}px))`;
    case DrawerSnapPoint.Partial: {
      const val = typeof partialHeight === 'string' ? partialHeight : `${partialHeight}px`;
      return `translateY(calc(100% - ${val}))`;
    }
    case DrawerSnapPoint.Full:
      return 'translateY(0)';
    default:
      return 'translateY(0)';
  }
}

/**
 * Drawer - A bottom drawer with 3 snap points and touch gestures
 *
 * Performance: transform and transition are managed imperatively (via refs),
 * never through React inline styles. This eliminates the frame-gap jump
 * between gesture end and React re-render.
 */
export function Drawer({
  currentState,
  onStateChange,
  minimizedHeight,
  partialHeight,
  fullHeight,
  header,
  children,
  footer,
  showHandle = true,
  enableGestures = true,
  dragThreshold = DEFAULT_DRAG_THRESHOLD,
  className,
  borderRadius = DEFAULT_BORDER_RADIUS,
  boxShadow = DEFAULT_BOX_SHADOW,
  backgroundColor = DEFAULT_BACKGROUND_COLOR,
  onDragEnd,
  zIndex = DEFAULT_Z_INDEX,
  onBackdropClick,
  expandOnScroll = false,
  bottomOffset = 0,
  partialDragRatio,
}: DrawerProps) {
  const contentRef = useRef<HTMLDivElement>(null);
  const prevStateRef = useRef(currentState);
  const isFirstRender = useRef(true);

  const animateToState = useCallback((el: HTMLElement, from: DrawerSnapPoint, to: DrawerSnapPoint) => {
    const computed = getComputedStyle(el).transform;
    el.style.transition = 'none';
    el.style.transform = computed || 'none';
    void el.offsetHeight; // force reflow so browser commits the starting position
    const transition = getSnapTransition(from, to);
    el.style.transition = transition;
    el.style.transform = getStateTransform(to, minimizedHeight, partialHeight);
  }, [minimizedHeight, partialHeight]);

  const {
    handleTouchStart,
    handleTouchMove,
    handleTouchEnd,
    containerRef,
  } = useDrawerGestures({
    currentState,
    onStateChange: newState => {
      const el = containerRef.current;
      const fromState = prevStateRef.current;
      prevStateRef.current = newState;

      if (el) {
        animateToState(el, fromState, newState);
      }
      if (newState !== currentState) {
        onStateChange(newState);
        onDragEnd?.(newState);
      }
    },
    dragThreshold,
    enabled: enableGestures,
    partialDragRatio,
  });

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;

    if (isFirstRender.current) {
      el.style.transform = getStateTransform(currentState, minimizedHeight, partialHeight);
      el.style.transition = 'none';
      isFirstRender.current = false;
      prevStateRef.current = currentState;
      return;
    }

    if (prevStateRef.current !== currentState) {
      animateToState(el, prevStateRef.current, currentState);
      prevStateRef.current = currentState;
    }
  }, [currentState, minimizedHeight, partialHeight, containerRef, animateToState]);

  useEffect(() => {
    if (typeof document === 'undefined') return;

    if (currentState === DrawerSnapPoint.Full) {
      const scrollY = window.scrollY;
      const timerId = setTimeout(() => {
        document.body.style.position = 'fixed';
        document.body.style.top = `-${scrollY}px`;
        document.body.style.width = '100%';
      }, SNAP_DURATION_TWO_STEP);

      return () => {
        clearTimeout(timerId);
        document.body.style.position = '';
        document.body.style.top = '';
        document.body.style.width = '';
        window.scrollTo(0, scrollY);
      };
    }
  }, [currentState]);

  useEffect(() => {
    if (!onBackdropClick || currentState === DrawerSnapPoint.Minimized) return;

    const handleOutsideTouch = (e: TouchEvent | MouseEvent) => {
      const container = containerRef.current;
      if (!container) return;
      const target = e.target as Node;
      if (!container.contains(target)) {
        onBackdropClick();
      }
    };

    document.addEventListener('touchstart', handleOutsideTouch, { passive: true });
    document.addEventListener('mousedown', handleOutsideTouch);

    return () => {
      document.removeEventListener('touchstart', handleOutsideTouch);
      document.removeEventListener('mousedown', handleOutsideTouch);
    };
  }, [onBackdropClick, currentState, containerRef]);

  const handleContentTouchStart = useCallback((e: React.TouchEvent) => {
    const content = contentRef.current;
    if (!content) return;
    if (content.scrollHeight > content.clientHeight) {
      e.stopPropagation();
    }
  }, []);

  useEffect(() => {
    if (!expandOnScroll || currentState === DrawerSnapPoint.Full) return;

    const content = contentRef.current;
    if (!content) return;

    const handleScroll = () => {
      if (content.scrollTop > 0) {
        content.scrollTop = 0;
        onStateChange(DrawerSnapPoint.Full);
      }
    };

    content.addEventListener('scroll', handleScroll, { passive: true });
    return () => {
      content.removeEventListener('scroll', handleScroll);
    };
  }, [expandOnScroll, currentState, onStateChange]);

  const getContentMaxHeight = (): string => {
    const totalHeight = typeof fullHeight === 'string' ? parseFloat(fullHeight) : fullHeight;
    const headerHeight = 70;
    return `${Math.max(0, totalHeight - headerHeight)}px`;
  };

  const drawerHeight = typeof fullHeight === 'string' ? fullHeight : `${fullHeight}px`;

  return (
    <div
      ref={containerRef}
      className={buildClassName(styles.drawer, styles[currentState], className)}
      style={{
        borderRadius: `${borderRadius}px ${borderRadius}px 0 0`,
        boxShadow,
        backgroundColor,
        zIndex,
        height: drawerHeight,
        bottom: bottomOffset > 0 ? `${bottomOffset}px` : undefined,
        paddingBottom: 'env(safe-area-inset-bottom)',
      }}
      onTouchStart={enableGestures ? handleTouchStart : undefined}
      onTouchMove={enableGestures ? handleTouchMove : undefined}
      onTouchEnd={enableGestures ? handleTouchEnd : undefined}
      role="dialog"
      aria-modal={currentState === DrawerSnapPoint.Full}
      aria-label="Drawer"
    >
      <div className={styles.header}>
        {header !== undefined ? (
          header
        ) : (
          <DrawerHeader showHandle={showHandle}>
            <div className={styles['default-header-content']} />
          </DrawerHeader>
        )}
      </div>

      <div
        ref={contentRef}
        className={styles.content}
        style={{ maxHeight: getContentMaxHeight() }}
        onTouchStart={handleContentTouchStart}
      >
        {children}
      </div>

      {footer && (
        <div className={styles.footer}>
          {footer}
        </div>
      )}
    </div>
  );
}
